'use client'

/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/no-misused-promises */

import React, { useState, useEffect, useRef } from 'react'
import Image from 'next/image'
import assetLoader from '@utils/internalImageLoader'
import { PrimaryButton } from '@acko-tech/building-blocks.ui.button'
import StyledComponentsRegistry from '@configs/registry'
import { validateVehicleNumber } from '@components/MainWidget/FastagWidget/helper'
import { vehicleInfoService } from '@services/vehicleInfoService'
import { ThemeProvider, AckoMainTheme } from '@acko-tech/building-blocks.ui.theme'
import { trackEvent } from '@acko-tech/building-blocks.ui.acko-analytics'
import { useEnvVariablesClientConfig } from '@context/EnvClientContext/EnvVariablesClientContext'
import {
    INPUT_ICON,
    INPUT_ICON_LOGO,
    TAP_BTN_EVENT,
    PHONE_NUMBER_ENTERED,
} from '@components/MainWidget/FastagWidget/constants'
import CreateLead from '@services/createLead'
import { segmentEventCommonPropFunc } from '@utils/segmentProperties'
import {
    VAS_WIDGET_INPUT_PLACEHOLDER,
    CHALLAN_WIDGET_INPUT,
    WIDGET_INPUT,
    REG_NO_ERROR,
    VEHICLE_DATA,
    LOCAL_STORAGE_KEY,
    RTO_VEHICLE_INFO_WIDGET_INPUT,
    WIDGET_CTA_TEXT_MAPPING,
} from '@components/MainWidget/ChallanWidgetLanding/constants'
import ReturningUserInfoBlock from '@components/MainWidget/ChallanWidgetLanding/returningUserInfoBlock'
import MobileNumberModal from '@components/MainWidget/ChallanWidgetModals/mobileNumberInputModal'
import { LOADING_SCREEN_CONTENT, MobileNumberActionType } from '@components/MainWidget/ChallanWidgetModals/constants'
import LoadingScreen from '@components/MainWidget/ChallanWidgetModals/LoadingScreen'
import { getVehicleMMVStoreData, fetchDataFromLS } from '@components/MainWidget/ChallanWidgetLanding/helper'
import { useUserContext } from '@context/UserContext/UserContenxt'
import { checkIsLoggedIn } from '@utils/client/isLoggedIn'
import { isChallanLandingWidget } from '@helpers/vasLandingPageHelper'
import {
    fetchWidgetApiKeyLeadFn,
    fetchWidgetProductFn,
    fetchWidgetSourceFn,
    widgetJourney,
} from '@configs/segmentEvents.config'
import { getRegNumberInfo } from '@components/WhatsappChallanNewFlow/helper'
import { useSearchParams } from 'next/navigation'
import { AuthLogin } from '@acko-tech/building-blocks.utils.auth-login-module'
import { handleMaharashtraChallan, handleNonVahanStateChallan, handleVahanStateChallan } from '@helpers/challanHelper'
import { fetchPPED } from '@services/fetchPped'
import { useFeatureValue } from '@growthbook/growthbook-react'
import logger from '@utils/logger'
import { getChallanPPEDBracket } from '@utils/ChallanPPEDBracket'
import ChallanAppRedirectWrapper from './AppRedirectModal/ChallanAppRedirectWrapper'
import styles from './ChallanWidgetLanding.module.scss'

interface ChallanRtoWidgetProps {
    widget: string
    widgetHeading?: string
    regexPatternsData?: object
    widgetCtaText?: string
    inputPlaceholder?: string
    isMobileDevice: boolean
    challanFinalScreenData: object
    widgetOptions: any
    segmentEventCommonProperties?: object
    isBottomFloater: boolean
    onHide: Function
}

type VehicleInfo = {
    registration_number: string,
    model_name: string,
    vehicle_type_v2: string,
    db_make_name: string,
    db_model_name: string,
    owner_name: string,
    fuel_type: string,
    previous_policy_expiry_detail: string,
    variant_id: number[]
}

type UserData = {
    mobile_number: string
    reg_number: string,
    vehicleInfo?: VehicleInfo
}

const ChallanRtoWidget: React.FC<ChallanRtoWidgetProps> = data => {
    const {
        widget,
        widgetHeading,
        regexPatternsData,
        widgetCtaText,
        inputPlaceholder,
        isMobileDevice,
        challanFinalScreenData,
        segmentEventCommonProperties,
        isBottomFloater,
        onHide,
    } = data

    const [regNo, setRegNo] = useState('')
    const [error, setError] = useState('')
    const { isLoggedIn, setUserLoggedIn, userData } = useUserContext()
    const [returningUserResponse, setReturningUserResponse] = useState<any>(null)
    const [challanRtoUserData, setChallanRtoUserData] = useState<UserData>({
        mobile_number: '',
        reg_number: '',
    })
    const [showChallanLoading, setShowChallanLoading] = useState<boolean>(false)
    const [showMobileNumberModal, setShowMobileNumberModal] = useState<boolean>(false)
    const [prefilledMobileNumber, setPrefilledMobileNumber] = useState<string | undefined>()
    const [showMobileChallanModal, setShowMobileChallanModal] = useState<boolean>(false)
    const [showMwebChallanModal, setShowMwebChallanModal] = useState<boolean>(true)
    const [isReturningUser, setIsReturningUser] = useState<boolean>(false)
    const inputPlaceHolder = inputPlaceholder || VAS_WIDGET_INPUT_PLACEHOLDER
    const inputRef = useRef<HTMLInputElement | null>(null)
    const config = useEnvVariablesClientConfig()
    const currentEnv = config?.ACKO_ENV
    const checkPPEDExperiment = useFeatureValue<boolean>('challan-pped-expt', false)
    const isChallanPage = isChallanLandingWidget(widget)
    const storageKey = LOCAL_STORAGE_KEY?.[widget] || VEHICLE_DATA

    const [properties, setProperties] = useState<any>(null)
    const [ppedData, setPpedData] = useState<Record<string, unknown> | null>(null)
    const [expiryBracket, setExpiryBracket] = useState<{
        isInBracket: boolean
        today: string
        od_expiry_date: string
        expirySource?: 'od' | 'tp'
        lowerBound: string
        upperBound: string
        daysFromExpiry: number
    }>()
    const [challanData, setChallanData] = useState<any>(null)

    const searchParams = useSearchParams()

    useEffect(() => {
        const fetchProperties = async () => {
            const resolvedProperties = await segmentEventCommonPropFunc(segmentEventCommonProperties)
            setProperties(resolvedProperties)
        }
        fetchProperties()
    }, [segmentEventCommonProperties])

    useEffect(() => {
        if (searchParams.get('regno')) {
            setRegNo(searchParams.get('regno')?.toUpperCase() || '')
        }
    }, [])

    useEffect(() => {
        const getLocalStorageData = async () => {
            const localStorageData = await fetchDataFromLS(storageKey)
            setReturningUserResponse(localStorageData)
            setChallanRtoUserData({ ...challanRtoUserData, mobile_number: localStorageData?.mobile_number })
        }
        getLocalStorageData()
    }, [showChallanLoading])

    const initializeLoginSdk = () => {
        // eslint-disable-next-line no-new
        new AuthLogin({
            // clientId: AUTH_FASTAG_CLIENT,
            onSuccess: async () => {
                setUserLoggedIn(true)
                const { user } = await checkIsLoggedIn()
                if (user?.phone) {
                    fetchVehicleAndProceed(regNo, user.phone)
                }
            },
            onClose: () => { },
            enableScrollOnClose: true,
        })
    }

    const handleReturningSearch = () => {
        setChallanRtoUserData(returningUserResponse)
        setIsReturningUser(true)
        setShowChallanLoading(true)
        onSubmitEvent(returningUserResponse, true)
    }

    const fetchVehicleAndProceed = async (regNumber, mobileNumber) => {
        if (!regNumber) {
            return
        }
        try {
            setShowChallanLoading(true)
            const vehicleInfo = await vehicleInfoService(regNumber)
            const vehicleImageData: any = await getVehicleMMVStoreData(storageKey, {
                ...challanRtoUserData,
                reg_number: regNumber,
                mobile_number: mobileNumber,
                vehicleInfo,
            })
            setChallanRtoUserData(prev => ({
                ...prev,
                reg_number: regNumber,
                mobile_number: mobileNumber,
                vehicleInfo,
                image: vehicleImageData?.image,
            }))
        } catch (error) {
            logger.error(`[Carbon Analytics Error log] Error fetching vehicle info: ${error}`, regNumber)
        }
    }

    const handleRegNoChange = e => {
        const newRegNumber = e?.target?.value.toUpperCase()
        setRegNo(newRegNumber)
        setChallanRtoUserData({ ...challanRtoUserData, reg_number: newRegNumber })
    }

    const onSubmitEvent = (data, isReturningUser = false) => {
        trackEvent(TAP_BTN_EVENT, {
            ...properties,
            journey: widgetJourney?.[widget],
            product: fetchWidgetProductFn?.[widget]?.(),
            phone: data?.mobile_number || '',
            registration_number: data?.reg_number || '',
            cta_text: isReturningUser ? 'Returning user' : 'New user',
        })
    }

    const onFormSubmit = e => {
        // debugger
        setError('')
        e?.preventDefault()
        inputRef?.current?.blur()
        const { isNonVahanState } = getRegNumberInfo(regNo)
        const vehicleError = validateVehicleNumber(regNo, regexPatternsData)
        if (!regNo || vehicleError) {
            setError(REG_NO_ERROR)
            return
        }
        onSubmitEvent(challanRtoUserData)

        if (isLoggedIn && userData) {
            fetchVehicleAndProceed(regNo, userData?.phone)
            return
        }
        if (isNonVahanState) {
            initializeLoginSdk()
            return
        }
        if (returningUserResponse?.vehicleInfo?.registration_number !== regNo) {
            setPrefilledMobileNumber(
                challanRtoUserData?.mobile_number || returningUserResponse?.mobile_number || undefined
            )
            setShowMobileNumberModal(true)
        } else if (!challanRtoUserData?.mobile_number) {
            setPrefilledMobileNumber(undefined)
            setShowMobileNumberModal(true)
        } else {
            fetchVehicleAndProceed(regNo, challanRtoUserData?.mobile_number)
            CreateLead({
                registrationNumber: regNo,
                phoneNo: challanRtoUserData?.mobile_number,
                productKey: fetchWidgetProductFn?.[widget]?.(),
                sourceName: fetchWidgetSourceFn?.[widget]?.(),
                apiKey: fetchWidgetApiKeyLeadFn?.[widget]?.(currentEnv),
            })
        }
    }

    const handleSegmentEvent = (eventName: string, mobileNumber?: string, actionType?: MobileNumberActionType) => {
        trackEvent(eventName, {
            ...properties,
            journey: widgetJourney?.[widget],
            product: fetchWidgetProductFn?.[widget]?.(),
            phone: mobileNumber || '',
            registration_number: regNo,
            ...(actionType ? { action_type: actionType } : {}),
        })
    }

    const handleMobileNumberSubmit = async (mobileNumber: string, actionType: MobileNumberActionType) => {
        setShowMobileNumberModal(false)
        fetchVehicleAndProceed(regNo, mobileNumber)
        trackEvent(PHONE_NUMBER_ENTERED, {
            ...properties,
            journey: widgetJourney?.[widget],
            product: fetchWidgetProductFn?.[widget]?.(),
            phone: mobileNumber || '',
            registration_number: regNo,
            action_type: actionType,
        })
        CreateLead({
            registrationNumber: regNo,
            phoneNo: mobileNumber,
            productKey: fetchWidgetProductFn?.[widget]?.(),
            sourceName: fetchWidgetSourceFn?.[widget]?.(),
            apiKey: fetchWidgetApiKeyLeadFn?.[widget]?.(currentEnv),
        })
    }

    const handleLoadingComplete = async () => {
        if (challanRtoUserData?.vehicleInfo?.vehicle_type_v2 === 'PRIVATE_CAR') {
            const registrationNumber = isReturningUser ? returningUserResponse?.vehicleInfo?.registration_number : regNo
            const { stateName, isNonVahanState } = getRegNumberInfo(registrationNumber)
            const clearPpedAndChallan = () => {
                setPpedData(null)
                setChallanData(null)
                setExpiryBracket(undefined)
            }
            await fetchPPED({ regNo: registrationNumber }).then(async (policyData: any) => {
                if (!policyData) {
                    clearPpedAndChallan()
                    return
                }
                setPpedData(policyData)
                const expiryBracketResult = getChallanPPEDBracket(policyData)
                if (!expiryBracketResult) {
                    return
                }
                setExpiryBracket(expiryBracketResult)
                if (expiryBracketResult.isInBracket && checkPPEDExperiment) {
                    try {
                        const nextChallanData = isNonVahanState
                            ? stateName === 'Maharashtra'
                                ? await handleMaharashtraChallan(registrationNumber, currentEnv)
                                : await handleNonVahanStateChallan(registrationNumber, currentEnv)
                            : await handleVahanStateChallan(registrationNumber, currentEnv)
                        if (nextChallanData == null) {
                            setChallanData([])
                            return
                        }
                        setChallanData(nextChallanData)
                    } catch (e) {
                        setChallanData([])
                    }
                }
            }).catch(e => {
                logger.error(`[Carbon Analytics Error log] error fetching PPED data: ${e}`)
                clearPpedAndChallan()
            })
        }
        setShowChallanLoading(false)
        setShowMobileChallanModal(true)
        setShowMwebChallanModal(true)
    }

    const handleFallbackModal = () => {
        setShowChallanLoading(false)
        setShowMobileChallanModal(false)
        setRegNo('')
        setError('')
    }

    return (
        <ThemeProvider theme={AckoMainTheme}>
            <>
                <div>
                    {showChallanLoading && (
                        <LoadingScreen
                            isMobile={isMobileDevice}
                            screens={LOADING_SCREEN_CONTENT?.[widget] || LOADING_SCREEN_CONTENT?.Default}
                            duration={1200}
                            regNo={regNo || challanRtoUserData?.reg_number}
                            onComplete={() => handleLoadingComplete()}
                            isBottomFloater={isBottomFloater}
                            showLoadingScreen={showChallanLoading}
                        />
                    )}

                    {showMobileChallanModal && (
                        <ChallanAppRedirectWrapper
                            challanFinalScreenData={challanFinalScreenData}
                            isMobile={isMobileDevice}
                            regNum={challanRtoUserData?.reg_number}
                            mobileNo={challanRtoUserData?.mobile_number}
                            vehicleInfoData={challanRtoUserData}
                            segmentEventCommonProperties={properties}
                            setShowMobileChallanModal={handleFallbackModal}
                            isBottomFloater={isBottomFloater}
                            onHide={onHide}
                            showMwebChallanModal={showMwebChallanModal}
                            setShowMwebChallanModal={setShowMwebChallanModal}
                            widgetType={widget}
                            ppedData={ppedData}
                            expiryBracket={expiryBracket}
                            challanData={challanData}
                            checkPPEDExperiment={checkPPEDExperiment}
                        />
                    )}
                    {showMobileNumberModal && (
                        <MobileNumberModal
                            isMobile={isMobileDevice}
                            showMobileNumberModal={showMobileNumberModal}
                            setShowMobileNumberModal={setShowMobileNumberModal}
                            onSubmit={(mobileNumber: string, actionType: MobileNumberActionType) => {
                                handleMobileNumberSubmit(mobileNumber, actionType).catch(error => {
                                    logger.error(`[Carbon Analytics Error log] error submitting mobile number: ${error}`)
                                })
                            }}
                            widgetType={widget}
                            mobileNumber={prefilledMobileNumber}
                            handleSegmentEvent={handleSegmentEvent}
                        />
                    )}
                </div>
                <div id={`${widgetJourney[widget]}-widget-landing-widget`} className={styles.widgetRootContainer}>
                    {returningUserResponse?.vehicleInfo?.registration_number && (
                        <ReturningUserInfoBlock
                            userInfo={returningUserResponse}
                            handleReturningSearch={handleReturningSearch}
                            widgetType={widget}
                        />
                    )}
                    <form className={`${styles.widgetForm}`} onSubmit={e => onFormSubmit(e)}>
                        <label htmlFor={WIDGET_INPUT} className={styles.widgetHeading}>
                            {widgetHeading}
                        </label>
                        <div className={error ? styles.inputErrorStyles : styles.inputBoxContainer}>
                            <Image
                                width={0}
                                height={0}
                                src={`${assetLoader()}${INPUT_ICON}`}
                                className={styles.inputIcon}
                                alt={INPUT_ICON_LOGO}
                            />
                            <input
                                type="text"
                                id={isChallanPage ? CHALLAN_WIDGET_INPUT : RTO_VEHICLE_INFO_WIDGET_INPUT}
                                ref={inputRef}
                                placeholder={inputPlaceHolder}
                                onChange={handleRegNoChange}
                                autoComplete="off"
                                value={regNo}
                                maxLength={10}
                            />
                        </div>
                        {error && <div className={styles.inputError}>{error}</div>}
                        <StyledComponentsRegistry>
                            <PrimaryButton className={styles.primaryButton}>
                                {widgetCtaText || WIDGET_CTA_TEXT_MAPPING[widget]}
                            </PrimaryButton>
                        </StyledComponentsRegistry>
                    </form>
                </div>
            </>
        </ThemeProvider>
    )
}

export default ChallanRtoWidget
